import { Block, EmptyState, Freshness, LoadingBlock, Metric, MetricGrid, Panel, SectionHeader } from "../components/ui";
import { Chart, type ChartDatum } from "../components/chart";
import { DataTable } from "../components/table";
import { num } from "../lib/format";

/**
 * Weekly accessibility count (a11y-21). Mirrors `_admin/a11yReport`, written
 * by `scripts/report-a11y.mjs --publish` after the a11y E2E
 * (`.github/workflows/a11y-report.yml`, Mondays): every axe violation of every
 * impact, counted per rule, newest week first.
 */
export type A11yImpact = "critical" | "serious" | "moderate" | "minor";

export interface A11yWeekRule {
  id: string;
  impact: A11yImpact;
  help: string;
  /** Failing elements over every screen × device run. */
  nodes: number;
  /** Screen × device runs where the rule fails. */
  screens: number;
}

export interface A11yWeek {
  week: string;
  runs: number;
  totals: Record<A11yImpact, number>;
  rules: A11yWeekRule[];
}

export interface A11yReportDoc {
  updatedAt?: string;
  weeks: A11yWeek[];
}

const IMPACTS: A11yImpact[] = ["critical", "serious", "moderate", "minor"];

interface Props {
  /** null while the listener has not answered; undefined when the document does not exist yet. */
  report: A11yReportDoc | null | undefined;
  listenerFailed?: boolean;
}

type RuleRow = A11yWeekRule & { delta: number | null };

export function AccessibilitySection({ report, listenerFailed = false }: Props) {
  const weeks = report?.weeks ?? [];
  const latest = weeks[0] ?? null;
  const previous = weeks[1] ?? null;
  const before = new Map((previous?.rules ?? []).map((rule) => [rule.id, rule.nodes]));
  const rows: RuleRow[] = (latest?.rules ?? []).map((rule) => ({
    ...rule,
    delta: previous ? rule.nodes - (before.get(rule.id) ?? 0) : null,
  }));
  const gone = previous ? previous.rules.filter((rule) => !latest?.rules.some((r) => r.id === rule.id)) : [];
  const trend: ChartDatum[] = [...weeks].reverse().map((week) => ({
    x: week.week,
    xLabel: week.week.slice(5),
    values: { critical: week.totals.critical + week.totals.serious, other: week.totals.moderate + week.totals.minor },
  }));
  const delta = (impact: A11yImpact) => {
    if (!latest || !previous) return { value: null, caption: "first week" };
    const change = latest.totals[impact] - previous.totals[impact];
    return { value: change, label: change > 0 ? `+${num(change)}` : num(change), goodWhen: "down" as const, caption: "vs previous week" };
  };

  return (
    <div>
      <SectionHeader
        title="Accessibility"
        subtitle="axe-core on the 22 screens of the gate (the whole app with the deck, an offer's detail and a thread, onboarding, sign-in and one page of each public family), desktop and phone. Serious and critical fail the release gate; moderate and minor are counted here every Monday."
        meta={
          <Freshness
            at={report?.updatedAt ? Date.parse(report.updatedAt) : null}
            failed={listenerFailed}
            source="_admin/a11yReport"
            staleAfterMs={8 * 24 * 3_600_000}
          />
        }
      />

      {report === null && <LoadingBlock metrics={4} rows={3} label="Loading the accessibility report" />}
      {report !== null && !latest && (
        <EmptyState message="No report yet — the weekly job (a11y-report.yml) writes the first one on Monday, or run `pnpm report:a11y -- --publish` after the a11y E2E." />
      )}

      {latest && (
        <>
          <MetricGrid cols={4} className="mb-8">
            {IMPACTS.map((impact) => (
              <Metric
                key={impact}
                label={`${impact[0]!.toUpperCase()}${impact.slice(1)} (elements)`}
                value={num(latest.totals[impact])}
                tone={(impact === "critical" || impact === "serious") && latest.totals[impact] > 0 ? "warn" : "default"}
                delta={delta(impact)}
              />
            ))}
          </MetricGrid>

          {weeks.length > 1 && (
            <Block title="By week">
              <Panel>
                <Chart
                  title="Failing elements per week"
                  data={trend}
                  series={[
                    { key: "critical", label: "Serious + critical", color: "var(--chart-1)", kind: "line" },
                    { key: "other", label: "Moderate + minor", color: "var(--chart-2)", kind: "line" },
                  ]}
                  format={num}
                  height={160}
                />
              </Panel>
            </Block>
          )}

          <Block title={`Rules failing in ${latest.week} (${latest.runs} runs)`} className="mb-0">
            <DataTable
              caption="axe rules failing this week"
              rows={rows}
              rowKey={(r) => r.id}
              emptyMessage="No axe rule fails."
              defaultSort={{ key: "nodes", dir: "desc" }}
              columns={[
                { key: "id", header: "Rule", cell: (r) => <span className="font-mono">{r.id}</span>, sortValue: (r) => r.id },
                { key: "impact", header: "Impact", cell: (r) => r.impact, sortValue: (r) => IMPACTS.indexOf(r.impact) },
                { key: "help", header: "What it checks", cell: (r) => r.help },
                { key: "nodes", header: "Elements", align: "right", cell: (r) => num(r.nodes), sortValue: (r) => r.nodes },
                { key: "screens", header: "Runs", align: "right", cell: (r) => num(r.screens), sortValue: (r) => r.screens },
                {
                  key: "delta",
                  header: "vs previous week",
                  align: "right",
                  cell: (r) => (r.delta == null ? "—" : r.delta > 0 ? `+${num(r.delta)}` : num(r.delta)),
                  sortValue: (r) => r.delta,
                },
              ]}
            />
            {gone.length > 0 && (
              <p className="mt-3 text-label text-muted-foreground">
                Fixed since last week: {gone.map((rule) => rule.id).join(", ")}.
              </p>
            )}
          </Block>
        </>
      )}
    </div>
  );
}
